import { playback } from '../state/store.js';
import { publisher } from '../publisher/mode.js';
import type { ReaderChapter } from '../state/chapter-store.js';

/**
 * Audio-only chapter view — rendered when the active publisher manifest has
 * `hasText: false`, or when the chapter happens to have zero verses. Keeps
 * the existing PlayerBar (book/chapter nav, play/pause, seek) and replaces
 * the karaoke verse list with a centered media-focused layout:
 *
 *   eyebrow:   "AUDIO BIBLE · {displayName from publisher}"
 *   title:     "{bookName} {chapter}"  (source-language book name)
 *   card:      elapsed | duration  + hairline progress
 *   meter:     5 thin bars, animated only while playing
 *   footnote:  "Audio-only · no text version published · {iso}"
 *
 * No new state, no new signals. Subscribes to the existing `playback` +
 * `publisher` signals. PlayerBar continues to drive playback as it does
 * for the karaoke view.
 */

type Props = {
  chapter: ReaderChapter;
};

export function AudioOnlyChapter({ chapter }: Props) {
  const p = playback.value;
  const pub = publisher.value;
  const eyebrowDetail = pub?.displayName ?? pub?.appName ?? '';
  const iso = chapter.language.iso639_3;
  const pct = p.durationSec > 0 ? (p.currentSec / p.durationSec) * 100 : 0;

  return (
    <article
      class="chapter audio-only-chapter"
      data-chapter-key={`${chapter.bookId}.${chapter.chapter}`}
    >
      <p class="audio-only-eyebrow">
        Audio bible{eyebrowDetail ? <span class="audio-only-eyebrow-sep"> · </span> : null}
        {eyebrowDetail ? <span class="audio-only-eyebrow-detail">{eyebrowDetail}</span> : null}
      </p>
      <h1 class="audio-only-title">
        {chapter.bookName} {chapter.chapter}
      </h1>

      <div class="audio-only-card" role="status" aria-live="off">
        <div class="audio-only-times">
          <span class="audio-only-time audio-only-time-elapsed">{fmt(p.currentSec)}</span>
          <span class="audio-only-time-divider" aria-hidden="true">/</span>
          <span class="audio-only-time audio-only-time-total">{fmt(p.durationSec)}</span>
        </div>
        <div class="audio-only-progress" aria-hidden="true">
          <div class="audio-only-progress-fill" style={{ width: `${pct}%` }} />
        </div>
      </div>

      <BeatMeter playing={p.playing} />

      <p class="audio-only-footnote">
        Audio-only edition · no text version published
        {iso ? <span class="audio-only-footnote-iso"> · {iso}</span> : null}
      </p>
    </article>
  );
}

function BeatMeter({ playing }: { playing: boolean }) {
  return (
    <div
      class={`audio-only-meter${playing ? ' is-playing' : ''}`}
      aria-hidden="true"
    >
      {[0, 1, 2, 3, 4].map((i) => (
        <span key={i} class="audio-only-meter-bar" style={{ animationDelay: `${i * 0.12}s` }} />
      ))}
    </div>
  );
}

function fmt(sec: number): string {
  if (!Number.isFinite(sec) || sec < 0) return '0:00';
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60).toString().padStart(2, '0');
  return `${m}:${s}`;
}
